"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface MessageCircleIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface MessageCircleIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const MessageCircleIcon = forwardRef<MessageCircleIconHandle, MessageCircleIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const svgVariants: Variants = {
      normal: { scale: 1, rotate: 0 },
      animate: {
        scale: [1, 1.05, 0.95, 1],
        rotate: [0, -2, 2, 0],
        transition: { duration: 1.1 * duration, ease: "easeInOut", repeat: 0 },
      },
    };

    const pathVariants: Variants = {
      normal: { pathLength: 1, opacity: 1 },
      animate: {
        pathLength: [0, 1],
        opacity: [0.6, 1],
        transition: { duration: 1.2 * duration, ease: "easeInOut", repeat: 0 },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        animate={controls}
        initial="normal"
        variants={svgVariants}
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.path
          d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"
          variants={pathVariants}
        />
      </motion.svg>
    );
  },
);

MessageCircleIcon.displayName = "MessageCircleIcon";
export { MessageCircleIcon };
